// apps/frontend/src/pages/pipelines/components/PipelineHealthIndicator.tsx
import { cn } from '@/lib/utils';
import { XCircle, AlertTriangle } from 'lucide-react';

interface PipelineHealthIndicatorProps {
  healthScore: number;
  errorCount?: number;
  warningCount?: number;
  className?: string;
}

export function PipelineHealthIndicator({
  healthScore,
  errorCount = 0,
  warningCount = 0,
  className,
}: PipelineHealthIndicatorProps) {
  const getHealthStatus = (score: number) => {
    if (score >= 90) return { label: 'Excellent', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (score >= 70) return { label: 'Good', color: 'text-blue-600', bgColor: 'bg-blue-100' };
    if (score >= 50) return { label: 'Fair', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    return { label: 'Poor', color: 'text-red-600', bgColor: 'bg-red-100' };
  };

  const status = getHealthStatus(healthScore);
  const percentage = Math.max(0, Math.min(100, healthScore));

  return (
    <div className={cn('rounded-lg border bg-white p-4', className)}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium">Pipeline Health</h3>
        <span className={cn('text-xs font-semibold px-2 py-1 rounded', status.bgColor, status.color)}>
          {status.label}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl font-bold">{healthScore}</span>
        <span className="text-sm text-gray-500">/100</span>
      </div>

      {/* Progress bar */}
      <div
        className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-3"
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Pipeline health score"
      >
        <div
          className={cn(
            'h-full transition-all duration-300',
            healthScore >= 90 && 'bg-green-500',
            healthScore >= 70 && healthScore < 90 && 'bg-blue-500',
            healthScore >= 50 && healthScore < 70 && 'bg-yellow-500',
            healthScore < 50 && 'bg-red-500'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Issues summary */}
      {(errorCount > 0 || warningCount > 0) && (
        <div className="flex items-center gap-4 text-sm">
          {errorCount > 0 && (
            <div className="flex items-center gap-1 text-red-600">
              <XCircle className="h-4 w-4" aria-hidden="true" />
              <span>{errorCount} {errorCount === 1 ? 'error' : 'errors'}</span>
            </div>
          )}
          {warningCount > 0 && (
            <div className="flex items-center gap-1 text-yellow-600">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <span>{warningCount} {warningCount === 1 ? 'warning' : 'warnings'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
